import { assertNever } from "@impero-com/common/utils/obj";

import {
    ControlConclusionAnswerKind,
    ControlConclusionFieldKind,
    EffectivenessFieldTypeAnswer,
    SampleConclusionAnswerKind,
    SampleConclusionFieldKind,
    UpdateableTestingProgramBody,
    AdminEditViewAccessRights,
} from "api/generated/model";
import {
    UpdatableFieldGroup,
    FieldCondition,
    Field,
} from "components/fieldBuilder/models";
import { diffFieldGroup } from "components/fieldBuilder/utils/diff";
import { UserIdAndRights } from "components/form/rhfFields/rhfAccessRights";
import { buildAccessRightsChangeset } from "utils/accessRights";
import { TestingProgramAccessRights } from "utils/accessRights/models";

import {
    ControlConclusionField,
    ControlConclusionFieldType,
    SampleConclusionField,
    SampleConclusionFieldType,
} from "./components/ControlConclusionBuilder/schema";
import { TestingProgramFormSchema } from "./useTestingProgramViewFormSchema";

export function apiAccessRightToFormAccessRights(
    accessRights: AdminEditViewAccessRights
): UserIdAndRights<TestingProgramAccessRights> {
    const accessRightsView = accessRights.view.map((userId) => ({
        userId,
        right: "View" as const,
    }));
    const accessRightsEdit = accessRights.edit.map((userId) => ({
        userId,
        right: "Edit" as const,
    }));
    const accessRightsAdmin = accessRights.admin.map((userId) => ({
        userId,
        right: "Admin" as const,
    }));
    return [...accessRightsView, ...accessRightsEdit, ...accessRightsAdmin];
}

export function apiControlConclusionFieldKindToFormConclusionFieldKind(
    fieldType: ControlConclusionFieldKind
): ControlConclusionFieldType {
    switch (fieldType.type) {
        case "Effectiveness":
            return {
                type: "Effectiveness",
                options: {
                    effectiveOptionName: fieldType.effectiveOptionName,
                    partiallyEffectiveOptionName:
                        fieldType.partiallyEffectiveOptionName,
                    notEffectiveOptionName: fieldType.notEffectiveOptionName,
                },
            };
        case "Heading":
        case "TextArea":
        case "TextInput":
        case "Dropdown":
        case "FileUpload":
        case "NumericInput":
        case "CheckboxGroup":
        case "RadioButtonGroup":
            return fieldType;
        default:
            return assertNever(fieldType);
    }
}

export function formControlConclusionFieldKindToApiConclusionFieldKind(
    fieldType: ControlConclusionFieldType
): ControlConclusionFieldKind {
    switch (fieldType.type) {
        case "Effectiveness":
            return {
                type: "Effectiveness",
                effectiveOptionName: fieldType.options.effectiveOptionName,
                partiallyEffectiveOptionName:
                    fieldType.options.partiallyEffectiveOptionName,
                notEffectiveOptionName:
                    fieldType.options.notEffectiveOptionName,
            };
        case "Heading":
        case "TextArea":
        case "TextInput":
        case "Dropdown":
        case "FileUpload":
        case "NumericInput":
        case "CheckboxGroup":
        case "RadioButtonGroup":
            return fieldType;
        default:
            return assertNever(fieldType);
    }
}

export function formSampleConclusionFieldKindToApiConclusionFieldKind(
    fieldType: SampleConclusionFieldType
): SampleConclusionFieldKind {
    switch (fieldType.type) {
        case "Heading":
        case "TextArea":
        case "TextInput":
        case "Dropdown":
        case "FileUpload":
        case "NumericInput":
        case "CheckboxGroup":
        case "RadioButtonGroup":
            return fieldType;
        default:
            return assertNever(fieldType);
    }
}

// Workaround : the form works with string as a value for conditions
export function apiControlConclusionFieldConditionToFormFieldCondition(
    targetField: Pick<
        Field<ControlConclusionFieldKind, ControlConclusionAnswerKind>,
        "fieldType"
    >,
    condition: FieldCondition<
        ControlConclusionAnswerKind | SampleConclusionAnswerKind
    >
): FieldCondition<string> {
    let value = "";
    switch (condition.value.type) {
        case "Text":
        case "Option":
            value = condition.value.value;
            break;
        case "Effectiveness":
            if (targetField.fieldType.type !== "Effectiveness") {
                throw new Error(
                    "Effectiveness condition on a field of wrong type"
                );
            }
            switch (condition.value.value) {
                case "partiallyEffective":
                    value = targetField.fieldType.partiallyEffectiveOptionName;
                    break;
                case "notEffective":
                    value = targetField.fieldType.notEffectiveOptionName;
                    break;
                case "effective":
                    value = targetField.fieldType.effectiveOptionName;
                    break;
                default:
                    return assertNever(condition.value);
            }
            break;
        case "MultiOption":
        case "BlobIds":
            value = condition.value.value.join(",");
            break;
        case "Numeric":
            value = "" + condition.value.value;
            break;
        default:
            return assertNever(condition.value);
    }
    return {
        ...condition,
        value,
    };
}

// Workaround : the form works with string as a value for conditions
export function apiSampleConclusionFieldConditionToFormFieldCondition(
    condition: FieldCondition<SampleConclusionAnswerKind>
): FieldCondition<string> {
    let value = "";
    switch (condition.value.type) {
        case "Text":
        case "Option":
            value = condition.value.value;
            break;
        case "MultiOption":
        case "BlobIds":
            value = condition.value.value.join(",");
            break;
        case "Numeric":
            value = "" + condition.value.value;
            break;
        default:
            return assertNever(condition.value);
    }
    return {
        ...condition,
        value,
    };
}

export function controlConclusionFormFieldToApiFieldCondition(
    targetField: ControlConclusionField,
    conditionValue: string
): ControlConclusionAnswerKind {
    switch (targetField.fieldType.type) {
        case "Effectiveness":
            let effectivenessValue: EffectivenessFieldTypeAnswer;
            switch (conditionValue) {
                case targetField.fieldType.options.effectiveOptionName:
                    effectivenessValue = "effective";
                    break;
                case targetField.fieldType.options.notEffectiveOptionName:
                    effectivenessValue = "notEffective";
                    break;
                case targetField.fieldType.options.partiallyEffectiveOptionName:
                    effectivenessValue = "partiallyEffective";
                    break;
                default:
                    throw new Error(
                        "Condition matches no Effectiveness option"
                    );
            }
            return { type: "Effectiveness", value: effectivenessValue };
        case "Dropdown":
        case "RadioButtonGroup":
            return { type: "Option", value: conditionValue };
        case "CheckboxGroup":
            return { type: "MultiOption", value: [conditionValue] };
        case "Heading":
        case "TextArea":
        case "TextInput":
        case "FileUpload":
        case "NumericInput":
            throw new Error(
                "Can not depend on field of type " + targetField.fieldType.type
            );
        default:
            return assertNever(targetField.fieldType);
    }
}

export function sampleConclusionFormFieldToApiFieldCondition(
    targetField: SampleConclusionField,
    conditionValue: string
): SampleConclusionAnswerKind {
    switch (targetField.fieldType.type) {
        case "Dropdown":
        case "RadioButtonGroup":
            return { type: "Option", value: conditionValue };
        case "CheckboxGroup":
            return { type: "MultiOption", value: [conditionValue] };
        case "Heading":
        case "TextArea":
        case "TextInput":
        case "FileUpload":
        case "NumericInput":
            throw new Error(
                "Can not depend on field of type " + targetField.fieldType.type
            );
        default:
            return assertNever(targetField.fieldType);
    }
}

export function formUpdateTestingProgramToApiUpdatableTestingProgram(
    oldData: TestingProgramFormSchema,
    newData: TestingProgramFormSchema
): UpdateableTestingProgramBody {
    const name = oldData.name !== newData.name ? newData.name : undefined;
    const description =
        oldData.description !== newData.description
            ? newData.description
            : undefined;
    const accessRights = buildAccessRightsChangeset(
        oldData.accessRights,
        newData.accessRights
    );
    const findOldControlConclusionField = (
        fieldId: string
    ): ControlConclusionField | undefined => {
        return oldData.controlConclusionTasks.find((f) => f.id === fieldId);
    };
    const findNewControlConclusionField = (
        fieldId: string
    ): ControlConclusionField | undefined => {
        return newData.controlConclusionTasks.find((f) => f.id === fieldId);
    };
    const findOldSampleConclusionField = (
        fieldId: string
    ): SampleConclusionField | undefined => {
        return oldData.sampleConclusionTasks.find((f) => f.id === fieldId);
    };
    const findNewSampleConclusionField = (
        fieldId: string
    ): SampleConclusionField | undefined => {
        return newData.sampleConclusionTasks.find((f) => f.id === fieldId);
    };

    const controlConclusionFieldGroup: UpdatableFieldGroup<
        ControlConclusionFieldKind,
        ControlConclusionAnswerKind
    > = diffFieldGroup(
        {
            id: "fieldgroup",
            fields: oldData.controlConclusionTasks.map((field) => {
                let condition: FieldCondition<ControlConclusionAnswerKind> | null =
                    null;
                if (field.condition !== null) {
                    const targetField = findOldControlConclusionField(
                        field.condition.fieldId
                    );
                    if (targetField === undefined) {
                        throw new Error(
                            "Condition on a field that does not exist"
                        );
                    }
                    condition = {
                        ...field.condition,
                        value: controlConclusionFormFieldToApiFieldCondition(
                            targetField,
                            field.condition.value
                        ),
                    };
                }
                return {
                    ...field,
                    condition,
                    fieldType:
                        formControlConclusionFieldKindToApiConclusionFieldKind(
                            field.fieldType
                        ),
                };
            }),
        },
        {
            id: "fieldgroup",
            fields: newData.controlConclusionTasks.map((field) => {
                let condition: FieldCondition<ControlConclusionAnswerKind> | null =
                    null;
                if (field.condition !== null) {
                    const targetField = findNewControlConclusionField(
                        field.condition.fieldId
                    );
                    if (targetField === undefined) {
                        throw new Error(
                            "Condition on a field that does not exist"
                        );
                    }
                    condition = {
                        ...field.condition,
                        value: controlConclusionFormFieldToApiFieldCondition(
                            targetField,
                            field.condition.value
                        ),
                    };
                }
                return {
                    ...field,
                    condition,
                    fieldType:
                        formControlConclusionFieldKindToApiConclusionFieldKind(
                            field.fieldType
                        ),
                };
            }),
        }
    );
    const sampleConclusionFieldGroup: UpdatableFieldGroup<
        SampleConclusionFieldKind,
        SampleConclusionAnswerKind
    > = diffFieldGroup(
        {
            id: "fieldgroup",
            fields: oldData.sampleConclusionTasks.map((field) => {
                let condition: FieldCondition<SampleConclusionAnswerKind> | null =
                    null;
                if (field.condition !== null) {
                    const targetField = findOldSampleConclusionField(
                        field.condition.fieldId
                    );
                    if (targetField === undefined) {
                        throw new Error(
                            "Condition on a field that does not exist"
                        );
                    }
                    condition = {
                        ...field.condition,
                        value: sampleConclusionFormFieldToApiFieldCondition(
                            targetField,
                            field.condition.value
                        ),
                    };
                }
                return {
                    ...field,
                    condition,
                    fieldType:
                        formSampleConclusionFieldKindToApiConclusionFieldKind(
                            field.fieldType
                        ),
                };
            }),
        },
        {
            id: "fieldgroup",
            fields: newData.sampleConclusionTasks.map((field) => {
                let condition: FieldCondition<SampleConclusionAnswerKind> | null =
                    null;
                if (field.condition !== null) {
                    const targetField = findNewSampleConclusionField(
                        field.condition.fieldId
                    );
                    if (targetField === undefined) {
                        throw new Error(
                            "Condition on a field that does not exist"
                        );
                    }
                    condition = {
                        ...field.condition,
                        value: sampleConclusionFormFieldToApiFieldCondition(
                            targetField,
                            field.condition.value
                        ),
                    };
                }
                return {
                    ...field,
                    condition,
                    fieldType:
                        formSampleConclusionFieldKindToApiConclusionFieldKind(
                            field.fieldType
                        ),
                };
            }),
        }
    );

    return {
        name,
        description,
        accessRights:
            Object.keys(accessRights).length !== 0 ? accessRights : undefined,
        controlConclusionFieldGroup,
        sampleConclusionFieldGroup,
    };
}
